import type { VisitorContext } from "@topzle/api-client";
import { userLocalePreferences } from "@topzle/locale";
import { useEffect, useState } from "react";
import { api } from "./api";

// v2 intentionally drops the old IP-geolocation cache. Only the current
// Cloudflare country and device locale are eligible for caching.
const CACHE_KEY = "topzle.visitor-context.v2";
const CACHE_TTL_MS = 24 * 60 * 60 * 1_000;

type CachedVisitorContext = VisitorContext & { cachedAt: number };

let inFlight: Promise<VisitorContext> | null = null;

function browserDefaults(): VisitorContext {
  const preferences = userLocalePreferences();
  const locale = Array.isArray(preferences.locales)
    ? (preferences.locales[0] ?? "en")
    : String(preferences.locales);
  return {
    countryCode: null,
    countryName: null,
    city: null,
    region: null,
    timeZone: preferences.timeZone,
    locale,
    currencyCode: null,
    currencySymbol: null,
    callingCode: null,
    countryFlag: null,
    source: "unknown",
  };
}

function cachedContext(): VisitorContext | null {
  try {
    const value = JSON.parse(
      localStorage.getItem(CACHE_KEY) ?? "null",
    ) as CachedVisitorContext | null;
    if (!value || Date.now() - value.cachedAt > CACHE_TTL_MS) return null;
    const browser = browserDefaults();
    return {
      ...value,
      city: null,
      region: null,
      locale: browser.locale,
      timeZone: browser.timeZone,
    };
  } catch {
    return null;
  }
}

export function loadVisitorContext(): Promise<VisitorContext> {
  const cached = cachedContext();
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = api
    .visitorContext()
    .then((server) => {
      const browser = browserDefaults();
      const resolved = {
        ...server,
        locale: browser.locale || server.locale,
        timeZone: browser.timeZone || server.timeZone,
      };
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ...resolved, cachedAt: Date.now() }));
      } catch {
        // Storage can be disabled. Locale defaults still work for this visit.
      }
      return resolved;
    })
    .catch(() => browserDefaults())
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function useVisitorContext(): VisitorContext {
  const [context, setContext] = useState<VisitorContext>(
    () => cachedContext() ?? browserDefaults(),
  );
  useEffect(() => {
    let active = true;
    void loadVisitorContext().then((value) => {
      if (active) setContext(value);
    });
    return () => {
      active = false;
    };
  }, []);
  return context;
}
